<template>
  <div id="dialog-customerRegisterMulti">
    <div class="dialog">
      <span class="cls" @click="closeDialog"></span>

      <div class="paper">
        <div class="header">
          <div class="header-left">
            <h1>⚡ 빠른 고객 등록</h1>
            <span class="count-badge">{{ customers.length }}명</span>
          </div>
          <div class="header-right">
            <button class="add-row-btn" @click="addRow">
              <i class="ph ph-plus"></i>
              행 추가
            </button>
            <span class="close-btn" @click="closeDialog">✕</span>
          </div>
        </div>

        <div class="table-container">
          <table class="register-table">
            <thead>
              <tr>
                <th class="col-num">#</th>
                <th class="col-name">이름 <span class="required">*</span></th>
                <th class="col-phone">연락처</th>
                <th class="col-rrn">주민번호 앞</th>
                <th class="col-rrnLast">고유번호</th>
                <th class="col-relation">가족관계</th>
                <th class="col-address">주소</th>
                <th class="col-action"></th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="(customer, index) in customers" 
                :key="customer.id"
                :class="{ 'row-filled': isRowFilled(customer) }"
              >
                <td class="col-num">{{ index + 1 }}</td>
                <td class="col-name">
                  <input
                    :ref="el => setInputRef(el, index, 'name')"
                    type="text"
                    v-model="customer.name"
                    placeholder="홍길동"
                    @keydown="handleKeydown($event, index, 'name')"
                    @input="autoAddRow(index)"
                  />
                </td>
                <td class="col-phone">
                  <input
                    :ref="el => setInputRef(el, index, 'phone')"
                    type="tel"
                    v-model="customer.phone"
                    placeholder="01012345678"
                    maxlength="11"
                    @keydown="handleKeydown($event, index, 'phone')"
                    @input="formatPhone(index)"
                  />
                </td>
                <td class="col-rrn">
                  <input
                    :ref="el => setInputRef(el, index, 'rrn')"
                    type="text"
                    v-model="customer.rrnFirst"
                    placeholder="990101"
                    maxlength="6"
                    @keydown="handleKeydown($event, index, 'rrn')"
                    @input="formatRrn(index)"
                  />
                </td>
                <td class="col-rrnLast">
                  <input
                    :ref="el => setInputRef(el, index, 'rrnLast')"
                    type="password"
                    v-model="customer.rrnLast"
                    placeholder="●●●●●●●"
                    maxlength="7"
                    @keydown="handleKeydown($event, index, 'rrnLast')"
                    @input="formatRrnLast(index)"
                  />
                </td>
                <td class="col-relation">
                  <div v-if="index === 0" class="relation-base">
                    <i class="ph ph-star"></i>
                    <span>기준</span>
                  </div>
                  <select
                    v-else
                    :ref="el => setInputRef(el, index, 'relation')"
                    v-model="customer.relationToBase"
                    class="relation-select"
                    @keydown="handleKeydown($event, index, 'relation')"
                  >
                    <option value="">관계 선택</option>
                    <option v-for="type in relationTypes" :key="type" :value="type">
                      {{ baseName }}의 {{ type }}
                    </option>
                  </select>
                </td>
                <td class="col-address">
                  <div class="address-wrapper">
                    <input
                      :ref="el => setInputRef(el, index, 'address')"
                      type="text"
                      v-model="customer.address"
                      placeholder="주소 입력 또는 검색"
                      @keydown="handleKeydown($event, index, 'address')"
                    />
                    <button class="search-btn" @click="openDaumPostcode(index)" tabindex="-1">
                      <i class="ph ph-magnifying-glass"></i>
                    </button>
                  </div>
                </td>
                <td class="col-action">
                  <button 
                    class="delete-btn" 
                    @click="removeRow(index)"
                    :disabled="customers.length === 1"
                    tabindex="-1"
                  >
                    <i class="ph ph-x"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 관계 미리보기 -->
        <div class="relation-preview" v-if="relationPairs.length > 0">
          <div class="preview-header">
            <i class="ph ph-users-three"></i>
            <span>등록될 가족관계</span>
            <span class="preview-count">{{ relationPairs.length * 2 }}건</span>
          </div>
          <div class="preview-tags">
            <span 
              v-for="(pair, index) in relationPairs" 
              :key="index"
              class="relation-tag"
            >
              {{ pair.baseName }}의 {{ pair.type }} → {{ pair.targetName }}
            </span>
          </div>
        </div>

        <div class="footer">
          <div class="footer-left">
            <p class="hint">
              <i class="ph ph-info"></i>
              <span><kbd>Tab</kbd> 또는 <kbd>Enter</kbd>로 다음 칸 이동 · <kbd>↓</kbd> 다음 행 · <kbd>Shift+Enter</kbd> 행 추가</span>
            </p>
          </div>
          <div class="footer-right">
            <button class="cancel-btn" @click="closeDialog">취소</button>
            <button 
              class="save-btn" 
              @click="saveAll"
              :disabled="validCount === 0 || isSaving"
            >
              <i class="ph ph-check" v-if="!isSaving"></i>
              <i class="ph ph-spinner ph-spin" v-else></i>
              {{ isSaving ? '저장 중...' : `${validCount}명 저장` }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { inject, ref, computed, onMounted, nextTick } from "vue";
import { useBackButton } from '@/utils/useBackBtn';

export default {
  emits: ['submit', 'close'],
  props: {
    registerDialogSwitch: Function,
    reloadForSync: Function,
  },
  setup(props, { emit }) {
    const sql_query = inject('sql_query');
    const member_info = inject('member_info');
    const checkCustomerMilestone = inject('checkCustomerMilestone');
    
    const inputRefs = ref({});
    const isSaving = ref(false);

    const relationTypes = [
      "아버지",
      "어머니",
      "자녀",
      "배우자",
      "형제자매",
      "조부",
      "조모",
      "외조부",
      "외조모",
      "손자녀",
      "외손자녀",
      "사촌",
      "친구",
      "동료",
      "지인",
      "스승",
      "제자",
      "선배",
      "후배"
    ];

    // 성별 판단 (주민번호 뒷자리 기준: 1,3 = 남자, 2,4 = 여자)
    function isMale(rrnLast) {
      return rrnLast === '1' || rrnLast === '3';
    }

    // 역관계 계산 함수
    function getReverseRelation(type, targetRrnLast) {
      const male = isMale(targetRrnLast);
      
      const reverseMap = {
        "아버지": "자녀",
        "어머니": "자녀",
        "자녀": male ? "아버지" : "어머니",
        "배우자": "배우자",
        "형제자매": "형제자매",
        "조부": "손자녀",
        "조모": "손자녀",
        "외조부": "외손자녀",
        "외조모": "외손자녀",
        "손자녀": male ? "조부" : "조모",
        "외손자녀": male ? "외조부" : "외조모",
        "사촌": "사촌",
        "친구": "친구",
        "동료": "동료",
        "지인": "지인",
        "스승": "제자",
        "제자": "스승",
        "선배": "후배",
        "후배": "선배"
      };

      return reverseMap[type] || type;
    }

    // 기준(첫 번째 행) 이름
    const baseName = computed(() => {
      const name = customers.value[0]?.name?.trim();
      return name && name.length >= 2 ? name : '기준';
    });

    // 관계 쌍 목록 (미리보기용) - 기준과의 관계만
    const relationPairs = computed(() => {
      const baseCustomer = customers.value[0];
      if (!baseCustomer || baseCustomer.name.trim().length < 2) return [];
      
      const pairs = [];
      customers.value.forEach((customer, idx) => {
        if (idx === 0) return; // 기준은 스킵
        if (customer.name.trim().length < 2) return;
        if (!customer.relationToBase) return;
        
        pairs.push({
          baseName: baseCustomer.name,
          targetName: customer.name,
          type: customer.relationToBase
        });
      });
      return pairs;
    });

    // 초기 5행
    const createEmptyCustomer = () => ({
      id: Date.now() + Math.random(),
      name: '',
      phone: '',
      rrnFirst: '',
      rrnLast: '',
      address: '',
      exAddress: '',
      location: '',
      relationToBase: '', // 기준(첫 번째 행)과의 관계
    });

    const customers = ref([
      createEmptyCustomer(),
      createEmptyCustomer(),
      createEmptyCustomer(),
      createEmptyCustomer(),
      createEmptyCustomer(),
    ]);

    const columns = ['name', 'phone', 'rrn', 'rrnLast', 'relation', 'address'];

    // 유효한 행 개수 (이름이 있는 행)
    const validCount = computed(() => {
      return customers.value.filter(c => c.name.trim().length >= 2).length;
    });

    // 행이 채워졌는지 확인
    function isRowFilled(customer) {
      return customer.name.trim().length >= 2;
    }

    // input ref 설정
    function setInputRef(el, rowIndex, colName) {
      if (el) {
        const key = `${rowIndex}-${colName}`;
        inputRefs.value[key] = el;
      }
    }

    // 특정 셀로 포커스
    function focusCell(rowIndex, colName) {
      nextTick(() => {
        const key = `${rowIndex}-${colName}`;
        const input = inputRefs.value[key];
        if (input) {
          input.focus();
          if (input.select) input.select();
        }
      });
    }

    // 키보드 네비게이션
    function handleKeydown(event, rowIndex, colName) {
      const colIndex = columns.indexOf(colName);

      if (event.key === 'Tab' || event.key === 'Enter') {
        if (event.shiftKey && event.key === 'Enter') {
          event.preventDefault();
          addRow();
          focusCell(customers.value.length - 1, 'name');
          return;
        }

        event.preventDefault();

        if (event.shiftKey) {
          // Shift+Tab: 이전 칸
          let prevColIndex = colIndex - 1;
          
          // 첫 번째 행에서는 relation 스킵
          if (rowIndex === 0 && prevColIndex >= 0 && columns[prevColIndex] === 'relation') {
            prevColIndex--;
          }
          
          if (prevColIndex >= 0) {
            focusCell(rowIndex, columns[prevColIndex]);
          } else if (rowIndex > 0) {
            focusCell(rowIndex - 1, columns[columns.length - 1]);
          }
        } else {
          // Tab/Enter: 다음 칸
          let nextColIndex = colIndex + 1;
          
          // 첫 번째 행에서는 relation 스킵
          if (rowIndex === 0 && nextColIndex < columns.length && columns[nextColIndex] === 'relation') {
            nextColIndex++;
          }

          if (nextColIndex < columns.length) {
            focusCell(rowIndex, columns[nextColIndex]);
          } else if (rowIndex < customers.value.length - 1) {
            focusCell(rowIndex + 1, columns[0]);
          } else {
            addRow();
            focusCell(customers.value.length - 1, 'name');
          }
        }
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (rowIndex < customers.value.length - 1) {
          focusCell(rowIndex + 1, colName);
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (rowIndex > 0) {
          focusCell(rowIndex - 1, colName);
        }
      }
    }

    // 마지막 행에 입력 시 자동으로 행 추가
    function autoAddRow(index) {
      if (index === customers.value.length - 1 && customers.value[index].name.trim()) {
        if (customers.value.length < 50) {
          addRow();
        }
      }
    }

    // 행 추가
    function addRow() {
      if (customers.value.length < 50) {
        customers.value.push(createEmptyCustomer());
      }
    }

    // 행 삭제
    function removeRow(index) {
      if (customers.value.length > 1) {
        // 첫 번째 행(기준) 삭제 시 모든 관계 초기화
        if (index === 0) {
          customers.value.forEach(c => {
            c.relationToBase = '';
          });
        }
        
        customers.value.splice(index, 1);
      }
    }

    // 전화번호 포맷
    function formatPhone(index) {
      let value = customers.value[index].phone.replace(/[^0-9]/g, '');
      if (value.length > 11) value = value.slice(0, 11);
      customers.value[index].phone = value;
    }

    // 주민번호 앞자리 포맷
    function formatRrn(index) {
      let value = customers.value[index].rrnFirst.replace(/[^0-9]/g, '');
      if (value.length > 6) value = value.slice(0, 6);
      customers.value[index].rrnFirst = value;
    }

    // 주민번호 뒷자리 포맷
    function formatRrnLast(index) {
      let value = customers.value[index].rrnLast.replace(/[^0-9]/g, '');
      if (value.length > 7) value = value.slice(0, 7);
      customers.value[index].rrnLast = value;
    }

    // 다음 우편번호 검색
    function openDaumPostcode(index) {
      new window.daum.Postcode({
        oncomplete: function(data) {
          customers.value[index].address = data.address + (data.buildingName ? ' ' + data.buildingName : '');
          customers.value[index].location = data.sido + ' ' + data.sigungu;
        }
      }).open();
    }

    // 이름 정제
    function sanitizeName(name) {
      if (!name) return null;

      const cleaned = name
        .replace(/\s+/g, ' ')
        .replace(/[^\p{L}\p{N} ]/gu, '')
        .trim();

      if (cleaned.length < 2) return null;
      if (cleaned.length > 10) return cleaned.slice(0, 10);

      return cleaned;
    }

    function escapeStr(value) {
      return value !== null && value !== undefined ? String(value).replace(/'/g, "\\'") : null;
    }

    // 전체 저장
    async function saveAll() {
      const validCustomers = customers.value.filter(c => {
        const name = sanitizeName(c.name);
        return name !== null;
      });

      if (validCustomers.length === 0) {
        alert('저장할 고객이 없습니다. 이름을 2자 이상 입력해주세요.');
        return;
      }

      isSaving.value = true;

      try {
        // 1단계: 모든 고객 정보 먼저 저장
        const savedCustomerMap = {}; // originalIndex -> savedIdx
        let savedCount = 0;

        for (let i = 0; i < customers.value.length; i++) {
          const c = customers.value[i];
          const name = sanitizeName(c.name);
          
          if (name === null) continue;

          // 변경 후
          let rrnValue = '';
          if (c.rrnFirst && c.rrnLast) {
            rrnValue = `${c.rrnFirst}-${c.rrnLast}`;
          } else if (c.rrnFirst) {
            rrnValue = c.rrnFirst;
          }
          
          const sql = `INSERT INTO customers SET
            mb_id = '${member_info.mb_id}',
            name = '${escapeStr(name)}',
            title = '고객님',
            rrn = ${rrnValue ? `'${escapeStr(rrnValue)}'` : 'NULL'},
            address = ${c.address ? `'${escapeStr(c.address)}'` : 'NULL'},
            ex_address = ${c.exAddress ? `'${escapeStr(c.exAddress)}'` : 'NULL'},
            location = ${c.location ? `'${escapeStr(c.location)}'` : 'NULL'},
            phone = ${c.phone ? `'${escapeStr(c.phone)}'` : 'NULL'}`;

          const result = await sql_query(sql);

          // 등록 성공 후 자동으로 마일스톤 체크
          checkCustomerMilestone(member_info.mb_id);
          
          let newIdx = null;
          if (result && result.insertId) {
            newIdx = result.insertId;
          } else {
            const idResult = await sql_query('SELECT LAST_INSERT_ID() as idx');
            newIdx = idResult[0]?.idx;
          }
          
          console.log(`고객 ${name} 저장됨, idx:`, newIdx);
          savedCustomerMap[i] = newIdx;
          savedCount++;
          window.dispatchEvent(new CustomEvent('customer-updated'));
        }

        // 2단계: 관계 일괄 저장 (기준과의 관계 + 역관계 자동 생성)
        let relationCount = 0;
        const baseDbIdx = savedCustomerMap[0]; // 첫 번째 행(기준)의 DB idx
        const baseCustomer = customers.value[0];

        if (baseDbIdx && baseCustomer.name.trim().length >= 2) {
          for (let i = 1; i < customers.value.length; i++) {
            const customer = customers.value[i];
            const customerDbIdx = savedCustomerMap[i];
            
            if (!customerDbIdx || !customer.relationToBase) continue;

            // 정방향: 기준 → 이 사람 (기준의 OO)
            const sql1 = `INSERT INTO customer_relations SET
              customer_idx = ${baseDbIdx},
              link_to = ${customerDbIdx},
              relation_type = '${escapeStr(customer.relationToBase)}'`;
            
            console.log('정방향 관계 저장:', sql1);
            await sql_query(sql1);
            relationCount++;

            // 역방향: 이 사람 → 기준 (기준 성별 기준으로 역관계 계산)
            const reverseType = getReverseRelation(customer.relationToBase, baseCustomer.rrnLast);
            const sql2 = `INSERT INTO customer_relations SET
              customer_idx = ${customerDbIdx},
              link_to = ${baseDbIdx},
              relation_type = '${escapeStr(reverseType)}'`;
            
            console.log('역방향 관계 저장:', sql2);
            await sql_query(sql2);
            relationCount++;
          }
        }

        const message = relationCount > 0 
          ? `${savedCount}명의 고객과 ${relationCount}건의 가족관계가 등록되었습니다! 🎉`
          : `${savedCount}명의 고객이 등록되었습니다! 🎉`;
        
        alert(message);

        if (props.reloadForSync) {
          props.reloadForSync();
        }

        closeDialog();

      } catch (error) {
        console.error('저장 실패:', error);
        alert('저장 중 오류가 발생했습니다.');
      } finally {
        isSaving.value = false;
      }
    }

    function closeDialog() {
      if (props.registerDialogSwitch) {
        props.registerDialogSwitch(false);
      }
      emit('close');
    }

    useBackButton(() => {
      closeDialog();
    });

    // 첫 번째 셀에 포커스
    onMounted(() => {
      nextTick(() => {
        focusCell(0, 'name');
      });
    });

    return {
      customers,
      validCount,
      isSaving,
      isRowFilled,
      setInputRef,
      handleKeydown,
      autoAddRow,
      addRow,
      removeRow,
      formatPhone,
      formatRrn,
      formatRrnLast,
      openDaumPostcode,
      saveAll,
      closeDialog,
      relationTypes,
      relationPairs,
      baseName,
    };
  }
}
</script>

<style scoped>
#dialog-customerRegisterMulti {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  animation: fadeIn 0.2s ease-out;
  backdrop-filter: blur(8px);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.dialog {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.cls {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
}

.paper {
  width: 95%;
  max-width: 1400px;
  height: 90%;
  max-height: 850px;
  background: var(--color-background);
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;
  overflow: hidden;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-left h1 {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
}

.count-badge {
  background: var(--color-brand);
  color: var(--color-text-black);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.add-row-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: var(--color-surface-reverse);
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.2s;
}

.add-row-btn:hover {
  background: var(--color-brand);
  color: var(--color-text-black);
}

.close-btn {
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-size: var(--font-size-xl);
  color: var(--color-text-secondary);
  transition: all 0.2s;
  border-radius: var(--radius-md);
}

.close-btn:hover {
  color: var(--color-text-primary);
  background: var(--color-background-light);
}

/* Table Container */
.table-container {
  flex: 1;
  overflow: auto;
  padding: 0 24px;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: transparent;
}

.table-container::-webkit-scrollbar-thumb {
  background: var(--color-surface-reverse);
  border-radius: 4px;
}

/* Table */
.register-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.register-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

.register-table th {
  background: var(--color-background);
  padding: 12px 8px;
  text-align: left;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  border-bottom: 2px solid var(--color-background-light);
  white-space: nowrap;
}

.register-table th .required {
  color: var(--color-special-warning);
}

.register-table td {
  padding: 4px;
  vertical-align: middle;
  border-bottom: 1px solid var(--color-background-light);
}

/* Column Widths */
.col-num { width: 40px; text-align: center !important; }
.col-name { width: 12%; min-width: 100px; }
.col-phone { width: 12%; min-width: 110px; }
.col-rrn { width: 10%; min-width: 85px; }
.col-rrnLast { width: 10%; min-width: 90px; }
.col-relation { width: 18%; min-width: 160px; }
.col-address { width: auto; min-width: 180px; }
.col-action { width: 40px; }

/* Row Number */
.col-num {
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

/* Filled Row Indicator */
tr.row-filled .col-num {
  color: var(--color-brand);
}

tr.row-filled td:first-child::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--color-brand);
}

tr {
  position: relative;
}

/* Input Styles */
.register-table input {
  width: 100%;
  padding: 12px;
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  transition: all 0.15s ease;
  box-sizing: border-box;
}

.register-table input:focus {
  outline: none;
  border-color: var(--color-brand);
  background: var(--color-background);
  box-shadow: 0 0 0 3px rgba(255, 165, 0, 0.2);
}

.register-table input::placeholder {
  color: var(--color-text-secondary);
  opacity: 0.5;
}

/* Relation Base Badge */
.relation-base {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  background: var(--color-brand);
  color: var(--color-text-black);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
}

.relation-base i {
  font-size: var(--font-size-base);
}

/* Relation Select */
.relation-select {
  width: 100%;
  padding: 12px;
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.15s ease;
}

.relation-select:focus {
  outline: none;
  border-color: var(--color-brand);
  background: var(--color-background);
  box-shadow: 0 0 0 3px rgba(255, 165, 0, 0.2);
}

/* Address Wrapper */
.address-wrapper {
  display: flex;
  gap: 4px;
}

.address-wrapper input {
  flex: 1;
}

.search-btn {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-reverse);
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.search-btn:hover {
  background: var(--color-brand);
  color: var(--color-text-black);
}

/* Delete Button */
.delete-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.delete-btn:hover:not(:disabled) {
  background: var(--color-special-warning);
  color: white;
}

.delete-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Relation Preview */
.relation-preview {
  padding: 12px 24px;
  border-top: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.preview-header i {
  color: var(--color-brand);
}

.preview-count {
  background: var(--color-surface-reverse);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: var(--font-size-xs);
}

.preview-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.relation-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: var(--color-brand);
  color: var(--color-text-black);
  border-radius: 20px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

/* Footer */
.footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid var(--color-background-light);
  flex-shrink: 0;
  gap: 16px;
}

.footer-left {
  flex: 1;
}

.hint {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.hint kbd {
  background: var(--color-surface-reverse);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: inherit;
  font-size: var(--font-size-xs);
}

.footer-right {
  display: flex;
  gap: 12px;
}

.cancel-btn {
  padding: 12px 24px;
  background: var(--color-surface-reverse);
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: var(--color-background-light);
}

.save-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  background: var(--color-brand);
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-black);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 165, 0, 0.4);
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.ph-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 태블릿 (768px ~ 1024px) */
@media (min-width: 601px) and (max-width: 1024px) {
  .paper {
    width: 95%;
    max-width: 900px;
    height: 90%;
    border-radius: var(--radius-lg);
  }

  /* 주소 컬럼 숨김 */
  .col-address,
  .register-table th:nth-child(7),
  .register-table td:nth-child(7) {
    display: none;
  }

  /* 컬럼 너비 재조정 */
  .col-name { width: 18%; }
  .col-phone { width: 18%; }
  .col-rrn { width: 14%; }
  .col-rrnLast { width: 14%; }
  .col-relation { width: 25%; }

  .register-table input,
  .relation-select {
    padding: 10px;
    font-size: var(--font-size-sm);
  }

  .relation-base {
    padding: 8px 10px;
    font-size: var(--font-size-xs);
  }

  /* 힌트 간소화 */
  .hint span {
    display: none;
  }
  
  .hint::after {
    content: 'Tab/Enter로 이동';
  }
}
/* 태블릿 (601px ~ 1024px) - 가로 스크롤 + 첫 컬럼 고정 */
@media (min-width: 601px) and (max-width: 1024px) {
  .paper {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .table-container {
    padding: 0 16px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .register-table {
    min-width: 900px; /* 테이블 최소 너비 보장 */
  }

  /* 번호 + 이름 컬럼 고정 */
  .register-table th.col-num,
  .register-table td.col-num {
    position: sticky;
    left: 0;
    z-index: 2;
    background: var(--color-background);
  }

  .register-table th.col-name,
  .register-table td.col-name {
    position: sticky;
    left: 40px;
    z-index: 2;
    background: var(--color-background);
  }

  /* 고정 컬럼 그림자 */
  .register-table td.col-name::after {
    content: '';
    position: absolute;
    right: -8px;
    top: 0;
    bottom: 0;
    width: 8px;
    background: linear-gradient(to right, rgba(0,0,0,0.08), transparent);
    pointer-events: none;
  }

  .register-table thead th.col-name::after {
    content: '';
    position: absolute;
    right: -8px;
    top: 0;
    bottom: 0;
    width: 8px;
    background: linear-gradient(to right, rgba(0,0,0,0.08), transparent);
    pointer-events: none;
  }

  /* 행 배경 처리 */
  .register-table tr.row-filled td.col-num,
  .register-table tr.row-filled td.col-name {
    background: var(--color-background);
  }

  /* 입력 필드 */
  .register-table input,
  .relation-select {
    padding: 10px 8px;
    font-size: var(--font-size-sm);
  }

  /* 힌트 - 스크롤 안내 추가 */
  .hint span {
    display: none;
  }
  
  .hint::after {
    content: '← → 스크롤로 더 많은 항목';
    font-size: var(--font-size-xs);
  }

  /* 푸터 */
  .footer {
    padding: 12px 16px;
  }
}

/* 모바일 (600px 이하) - 동일하게 가로 스크롤 */
@media (max-width: 600px) {
  .paper {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .header {
    padding: 12px 16px;
  }

  .header-left h1 {
    font-size: var(--font-size-base);
  }

  .add-row-btn {
    padding: 8px 12px;
    font-size: var(--font-size-sm);
  }

  .add-row-btn i {
    display: block;
  }

  /* 버튼 텍스트 숨김 */
  .add-row-btn {
    gap: 0;
  }

  .table-container {
    padding: 0 12px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .register-table {
    min-width: 700px;
  }

  /* 번호 + 이름 고정 */
  .register-table th.col-num,
  .register-table td.col-num {
    position: sticky;
    left: 0;
    z-index: 2;
    background: var(--color-background);
    min-width: 32px;
    width: 32px;
  }

  .register-table th.col-name,
  .register-table td.col-name {
    position: sticky;
    left: 32px;
    z-index: 2;
    background: var(--color-background);
    min-width: 90px;
  }

  .register-table td.col-name::after {
    content: '';
    position: absolute;
    right: -6px;
    top: 0;
    bottom: 0;
    width: 6px;
    background: linear-gradient(to right, rgba(0,0,0,0.1), transparent);
    pointer-events: none;
  }

  /* 주소 컬럼 숨김 (모바일에서는 너무 김) */
  .col-address,
  .register-table th:nth-child(7),
  .register-table td:nth-child(7) {
    display: none;
  }

  .register-table input,
  .relation-select {
    padding: 10px 6px;
    font-size: var(--font-size-sm);
  }

  .relation-base {
    padding: 8px;
    font-size: var(--font-size-xs);
    gap: 4px;
  }

  .relation-base span {
    display: none;
  }

  /* 관계 미리보기 간소화 */
  .relation-preview {
    padding: 8px 12px;
  }

  .preview-tags {
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
  }

  .relation-tag {
    flex-shrink: 0;
    font-size: var(--font-size-xs);
    padding: 4px 10px;
  }

  /* 푸터 */
  .footer {
    padding: 12px;
    flex-direction: column;
    gap: 8px;
  }

  .footer-left {
    display: none;
  }

  .footer-right {
    width: 100%;
    gap: 8px;
  }

  .cancel-btn,
  .save-btn {
    flex: 1;
    padding: 12px;
    font-size: var(--font-size-sm);
    justify-content: center;
  }
}
</style>